import React, { ReactNode, useState } from 'react';
import {
    Box,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    ListItemIcon,
    Tooltip,
    useMediaQuery,
    useTheme as useMuiTheme,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AppSwitcher } from 'ui-cross-app';
import { useNavigate, useLocation } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { subAppConfigs, subAppIsVisibleToRoles } from '../../config/subApps';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserProfile } from '../../redux/authSlice';
import { logoutAuthenticatedSession } from '../../services/auth/logoutSession';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import UserAvatar from '../UserAvatar';
import ProfilePictureModal from '../ProfilePictureModal';
import { RootState } from '../../redux/store';
import { useAiObservabilityAccess } from '../../features/aiObservability/useAiObservabilityAccess';
import { useChatDashboardAccess } from '../../features/chat/useChatDashboardAccess';
import { isSubAppVisible } from '../../config/subAppVisibility';

const SIDEBAR_WIDTH = 240;

interface MainLayoutProps {
  children: ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  console.log('🔐 [MainLayout] RENDER');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useMuiTheme();
  const { isDarkMode, toggleDarkMode } = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const flags = useFeatureFlags();
  const aiObservabilityAccess = useAiObservabilityAccess();
  const workerChatAccess = useAiObservabilityAccess(true);
  const chatDashboardAccess = useChatDashboardAccess();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profilePictureModalOpen, setProfilePictureModalOpen] = useState(false);

  const auth = useSelector((state: RootState) => state.auth);
  const currentUser = auth.user;

  const handleLogout = async () => {
    await logoutAuthenticatedSession(dispatch);
  };

  const handleProfilePictureSave = async (imageUrl: string | null) => {
    try {
      dispatch(updateUserProfile({ profilePicture: imageUrl || undefined }));
    } catch (error) {
      console.error('Failed to update profile picture:', error);
      throw error;
    }
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Nav items whose flag is on. The active item is the one with the LONGEST
  // matching path prefix, so a sub-route like /resources/easyflex highlights
  // only EasyFlex — not its /resources parent (both start with /resources).
  const visibleApps = subAppConfigs.filter((app) =>
    isSubAppVisible(app, flags, aiObservabilityAccess, workerChatAccess, chatDashboardAccess) &&
    subAppIsVisibleToRoles(app, [auth.role, ...(auth.user?.roles ?? [])]),
  );
  const activeNavPath = visibleApps
    .map((app) => app.menuItems[0].path)
    .filter((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0] ?? '';

  const userName = currentUser?.firstName && currentUser?.lastName
    ? `${currentUser.firstName} ${currentUser.lastName}`
    : currentUser?.username || 'User';

  const sidebarContent = (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    }}>
      {/* Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 2,
          cursor: 'pointer',
          flexShrink: 0,
        }}
        onClick={() => {
          navigate('/');
          if (isMobile) setDrawerOpen(false);
        }}
      >
        <img
          src="/optima-uitzendburo.svg"
          alt="Optima"
          style={{ height: 28 }}
        />
      </Box>

      {/* Primary nav */}
      <List sx={{ px: 1, flex: 1, overflow: 'auto' }}>
        {visibleApps.map((app) => {
          const active = app.menuItems[0].path === activeNavPath;
          return (
            <ListItem key={app.id} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => {
                  navigate(app.menuItems[0].path);
                  if (isMobile) setDrawerOpen(false);
                }}
                sx={{
                  borderRadius: 1.5,
                  py: 0.75,
                  px: 1.5,
                  backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: active
                      ? alpha(theme.palette.primary.main, 0.12)
                      : theme.palette.action.hover,
                  },
                }}
              >
                {app.icon && (
                  <ListItemIcon sx={{
                    minWidth: 36,
                    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                  }}>
                    {app.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={app.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: active ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Settings nav item */}
      <Box sx={{ px: 1, flexShrink: 0 }}>
        <ListItemButton
          onClick={() => {
            navigate('/settings');
            if (isMobile) setDrawerOpen(false);
          }}
          sx={{
            borderRadius: 1.5,
            py: 0.75,
            px: 1.5,
            color: isActive('/settings') ? theme.palette.primary.main : theme.palette.text.secondary,
            backgroundColor: isActive('/settings') ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
            '&:hover': {
              backgroundColor: isActive('/settings')
                ? alpha(theme.palette.primary.main, 0.12)
                : theme.palette.action.hover,
            },
          }}
        >
          <ListItemIcon sx={{
            minWidth: 36,
            color: isActive('/settings') ? theme.palette.primary.main : theme.palette.text.secondary,
          }}>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Settings"
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isActive('/settings') ? 600 : 500,
            }}
          />
        </ListItemButton>
      </Box>

      {/* ===== Footer: AppSwitcher → Quick Actions → User Chip ===== */}
      <Box sx={{ mt: 'auto', borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`, flexShrink: 0 }}>
        {/* AppSwitcher */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
        }}>
          <AppSwitcher currentAppId="optima" />
        </Box>

        {/* Quick Actions */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 0.5,
          py: 1,
        }}>
          <Tooltip title={isDarkMode ? 'Light mode' : 'Dark mode'} placement="top">
            <IconButton
              onClick={toggleDarkMode}
              size="small"
              sx={{ color: theme.palette.text.secondary }}
              aria-label={isDarkMode ? 'Light mode' : 'Dark mode'}
            >
              {isDarkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* User Chip */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            mx: 1,
            mb: 1,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.action.hover, 0.04),
            cursor: 'pointer',
          }}
          onClick={() => setProfilePictureModalOpen(true)}
        >
          <UserAvatar user={currentUser} size={36} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {userName}
            </Typography>
            {currentUser?.email && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                }}
              >
                {currentUser.email}
              </Typography>
            )}
          </Box>
          <Tooltip title="Logout" placement="top">
            <IconButton
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleLogout();
              }}
              size="small"
              sx={{ color: theme.palette.text.secondary }}
              aria-label="Logout"
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', minWidth: 0, width: '100%', overflow: 'hidden' }}>
        {/* Thin top bar */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 56,
          px: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
          flexShrink: 0,
        }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <img
              src="/optima-uitzendburo.svg"
              alt="Optima"
              style={{ height: 24 }}
            />
          </Box>
          <IconButton
            aria-label="open menu"
            onClick={() => setDrawerOpen(true)}
            sx={{ color: theme.palette.text.primary }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Mobile drawer */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}
        >
          {sidebarContent}
        </Drawer>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: '100%', overflow: 'auto' }}>
          {children}
        </Box>

        {currentUser && (
          <ProfilePictureModal
            open={profilePictureModalOpen}
            onClose={() => setProfilePictureModalOpen(false)}
            user={currentUser}
            onSave={handleProfilePictureSave}
          />
        )}
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box sx={{ display: 'flex', height: '100vh', minWidth: 0, width: '100%', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Box sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {sidebarContent}
      </Box>

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: '100%', overflow: 'auto', backgroundColor: theme.palette.background.default }}>
        {children}
      </Box>

      {currentUser && (
        <ProfilePictureModal
          open={profilePictureModalOpen}
          onClose={() => setProfilePictureModalOpen(false)}
          user={currentUser}
          onSave={handleProfilePictureSave}
        />
      )}
    </Box>
  );
};
